// RUTA: /src/hooks/useRoleManager.js
// Hook para gestionar asignación de roles (admin, tutor)

import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { toast } from 'react-hot-toast';

/**
 * Hook para gestionar roles de usuarios (admin/tutor)
 * @returns {Object} Handler para asignar roles
 */
export const useRoleManager = () => {
  
  const handleSetTutorRole = async (targetUserUID) => {
    // Get the current logged-in user
    const currentUser = auth.currentUser;

    if (!currentUser) {
      toast.error('Debes estar autenticado para asignar roles.');
      return;
    }

    if (!targetUserUID) {
      toast.error('Debes proporcionar el UID del usuario.');
      return;
    }

    console.log('🎯 Intentando asignar rol de tutor:', {
      targetUserUID,
      currentUserEmail: currentUser.email,
      currentUserUID: currentUser.uid
    });

    if (!window.confirm(`¿Seguro que quieres asignar el rol de TUTOR al usuario con UID: ${targetUserUID}?`)) {
      return;
    }

    toast.loading('Asignando rol de tutor...');

    const setTutorRoleFunction = httpsCallable(functions, 'setTutorRole');

    try {
      const result = await setTutorRoleFunction({
        userId: targetUserUID,
        isTutor: true
      });

      toast.dismiss();
      toast.success(result.data.message || '¡Rol de tutor asignado correctamente!');
      console.log('✅ Resultado de la función:', result.data);

      // If assigning to yourself, refresh the token
      if (targetUserUID === currentUser.uid) {
        console.log('Refrescando token para obtener los nuevos claims...');
        await currentUser.getIdToken(true); // Force token refresh
        await currentUser.reload(); // Reload user data
        toast.success('Token actualizado. Recargando página...');

        // Reload the page after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.success('¡Rol asignado! El usuario ya aparecerá en la lista de tutores.');
      }
    } catch (error) {
      toast.dismiss();
      toast.error(`Error al asignar el rol: ${error.message}`);
      console.error('Error detallado al asignar rol:', error);
    }
  };

  const handleSetFlashPartnerRole = async (targetUserUID) => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      toast.error('Debes estar autenticado para asignar roles.');
      return;
    }

    if (!targetUserUID) {
      toast.error('Debes proporcionar el UID del usuario.');
      return;
    }

    if (!window.confirm(`¿Seguro que quieres convertir al usuario con UID: ${targetUserUID} en Flash Partner? Tendrá acceso al portal de mentor y podrá ver TODOS los estudiantes.`)) {
      return;
    }

    toast.loading('Asignando rol de Flash Partner...');

    const setFlashPartnerRoleFunction = httpsCallable(functions, 'setFlashPartnerRole');

    try {
      const result = await setFlashPartnerRoleFunction({
        userId: targetUserUID,
        isFlashPartner: true
      });

      toast.dismiss();
      toast.success(result.data.message || '¡Rol de Flash Partner asignado correctamente!');

      if (targetUserUID === currentUser.uid) {
        await currentUser.getIdToken(true);
        await currentUser.reload();
        toast.success('Token actualizado. Recargando página...');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.success('¡Flash Partner asignado! El usuario puede acceder al portal de mentor y ver todos los estudiantes.');
      }
    } catch (error) {
      toast.dismiss();
      toast.error(`Error al asignar el rol: ${error.message}`);
      console.error('Error detallado al asignar Flash Partner:', error);
    }
  };

  return {
    handleSetTutorRole,
    handleSetFlashPartnerRole,
  };
};
